import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "../../context/LayoutContext";
import { assignSupportTicket } from "../../api/ticketAssignment.service";
import {
  API_LIST_TICKETS,
  API_UPDATE_TICKET_PRIORITY,
  API_UPDATE_TICKET_STATUS,
} from "../../api/ticket.service";
import {
  normalizeAssignedTo,
  TICKET_AGENT_OPTIONS,
} from "../../constants/ticketAgents";
import TicketListPane from "../../components/TicketWorkspace/TicketListPane";
import TicketConversationPane from "../../components/TicketWorkspace/TicketConversationPane";
import "./ticketWorkspace.css";

const PAGE_SIZE = 20;

const normalizeTicketsResponse = (response) => {
  const payload = response?.data || response || {};
  const rowsCandidates = [payload?.rows, payload?.data?.rows, payload?.tickets, payload?.data];
  const rows = rowsCandidates.find((item) => Array.isArray(item)) || [];
  const countCandidates = [payload?.count, payload?.data?.count, payload?.total];
  const countFound = countCandidates.find((item) => Number.isFinite(Number(item)));
  const count = countFound !== undefined ? Number(countFound) : rows.length;
  return { rows, count };
};

const normalizeError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const defaultDraft = {
  status: "",
  priority: "",
  assignedTo: "",
};

export const TicketWorkspace = () => {
  const currentUser = useSelector((state) => state.authenticate.user);
  const { toastMessage } = useLayout();
  const token = localStorage.getItem("token");

  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    priority: "",
    assignedTo: "",
  });

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState("");

  const [metaDraft, setMetaDraft] = useState(defaultDraft);
  const [savingMeta, setSavingMeta] = useState(false);
  const [assigningMeta, setAssigningMeta] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });

  const dirty = useMemo(() => {
    if (!selectedTicket) return false;
    return (
      String(metaDraft.status || "") !== String(selectedTicket.status || "") ||
      String(metaDraft.priority || "") !== String(selectedTicket.priority || "")
    );
  }, [metaDraft, selectedTicket]);

  const loadTicketsPage = useCallback(
    async ({ nextPage, replace }) => {
      const payload = {
        page: nextPage,
        limit: PAGE_SIZE,
        q: filters.q?.trim() || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        assignedTo: normalizeAssignedTo(filters.assignedTo) || undefined,
      };
      const response = await API_LIST_TICKETS(payload);
      const { rows, count } = normalizeTicketsResponse(response);

      setTickets((prev) => {
        const merged = replace ? rows : [...prev, ...rows];
        const byId = new Map();
        merged.forEach((item) => {
          byId.set(String(item.id), item);
        });
        return Array.from(byId.values());
      });

      const canLoadMoreByCount = (nextPage + 1) * PAGE_SIZE < count;
      const canLoadMoreBySize = rows.length === PAGE_SIZE && count === rows.length;
      setHasMore(canLoadMoreByCount || canLoadMoreBySize);
    },
    [filters.assignedTo, filters.priority, filters.q, filters.status]
  );

  const loadMore = async () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    setListError("");
    try {
      await loadTicketsPage({ nextPage, replace: false });
      setPage(nextPage);
    } catch (error) {
      setListError(normalizeError(error, "Unable to load more tickets."));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
      const run = async () => {
        setLoading(true);
        setListError("");
        setPage(0);
        try {
          await loadTicketsPage({ nextPage: 0, replace: true });
        } catch (error) {
          if (!alive) return;
          setListError(normalizeError(error, "Unable to load tickets."));
          setTickets([]);
          setHasMore(false);
        } finally {
          if (alive) setLoading(false);
        }
      };
      run();
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [filters.assignedTo, filters.q, filters.priority, filters.status, loadTicketsPage]);

  useEffect(() => {
    if (!selectedTicket) {
      setMetaDraft(defaultDraft);
      return;
    }
    setMetaDraft({
      status: selectedTicket.status || "",
      priority: selectedTicket.priority || "",
      assignedTo: normalizeAssignedTo(selectedTicket.assignedTo),
    });
    setSaveMessage({ type: "", text: "" });
    setAssignError("");
  }, [selectedTicket]);

  useEffect(() => {
    if (!selectedTicket && tickets.length > 0) {
      setSelectedTicket(tickets[0]);
    }
  }, [selectedTicket, tickets]);

  const updateTicketInListAndSelection = (ticketId, updater) => {
    setTickets((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(ticketId)
          ? {
              ...item,
              ...updater,
            }
          : item
      )
    );
    setSelectedTicket((prev) =>
      prev && Number(prev.id) === Number(ticketId)
        ? {
            ...prev,
            ...updater,
          }
        : prev
    );
  };

  const handleSaveMeta = async () => {
    if (!selectedTicket || !dirty || savingMeta) return;

    const ticketId = selectedTicket.id;
    const failures = [];
    setSavingMeta(true);
    setSaveMessage({ type: "", text: "" });

    try {
      if (String(metaDraft.status || "") !== String(selectedTicket.status || "")) {
        try {
          await API_UPDATE_TICKET_STATUS(ticketId, metaDraft.status);
        } catch (error) {
          failures.push(`Status: ${normalizeError(error, "update failed")}`);
        }
      }

      if (String(metaDraft.priority || "") !== String(selectedTicket.priority || "")) {
        try {
          await API_UPDATE_TICKET_PRIORITY(ticketId, metaDraft.priority);
        } catch (error) {
          failures.push(`Priority: ${normalizeError(error, "update failed")}`);
        }
      }

      if (failures.length > 0) {
        setSaveMessage({
          type: "error",
          text: `Partial update failed. ${failures.join(" | ")}`,
        });
        return;
      }

      updateTicketInListAndSelection(ticketId, {
        status: metaDraft.status,
        priority: metaDraft.priority,
      });
      setSaveMessage({ type: "success", text: "Ticket updated successfully." });
    } finally {
      setSavingMeta(false);
    }
  };

  const applyAssignUpdate = (ticketId, responseData, fallbackAssignedTo) => {
    const updatedTicket = responseData?.data || null;
    const nextAssignedTo =
      normalizeAssignedTo(updatedTicket?.assignedTo) ||
      normalizeAssignedTo(fallbackAssignedTo);

    updateTicketInListAndSelection(ticketId, {
      ...(updatedTicket || {}),
      assignedTo: nextAssignedTo,
    });
    setMetaDraft((prev) => ({ ...prev, assignedTo: nextAssignedTo }));
    setAssignError("");
  };

  const handleAssignMeta = async () => {
    if (!selectedTicket || assigningMeta) return;
    try {
      setAssigningMeta(true);
      const response = await assignSupportTicket(selectedTicket.id, metaDraft.assignedTo);
      applyAssignUpdate(selectedTicket.id, response, metaDraft.assignedTo);
      toastMessage({ severity: "success", detail: "Ticket assigned successfully" });
    } catch (error) {
      const message = error?.message || "Unable to assign ticket";
      setAssignError(message);
      toastMessage({ severity: "error", detail: message });
    } finally {
      setAssigningMeta(false);
    }
  };

  const handleUnassignMeta = async () => {
    if (!selectedTicket || assigningMeta) return;
    try {
      setAssigningMeta(true);
      const response = await assignSupportTicket(selectedTicket.id, null);
      applyAssignUpdate(selectedTicket.id, response, "");
      toastMessage({ severity: "success", detail: "Ticket unassigned successfully" });
    } catch (error) {
      const message = error?.message || "Unable to unassign ticket";
      setAssignError(message);
      toastMessage({ severity: "error", detail: message });
    } finally {
      setAssigningMeta(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      q: "",
      status: "",
      priority: "",
      assignedTo: "",
    });
  };

  return (
    <div className="ticket-workspace-page">
      <div className="ticket-workspace-shell">
        <TicketListPane
          tickets={tickets}
          selectedTicketId={selectedTicket?.id}
          filters={filters}
          loading={loading}
          loadingMore={loadingMore}
          error={listError}
          hasMore={hasMore}
          onSelectTicket={setSelectedTicket}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          onLoadMore={loadMore}
        />

        <TicketConversationPane
          selectedTicket={selectedTicket}
          token={token}
          currentUser={currentUser}
          metaDraft={metaDraft}
          dirty={dirty}
          savingMeta={savingMeta}
          assigningMeta={assigningMeta}
          assignError={assignError}
          saveMessage={saveMessage}
          agentOptions={TICKET_AGENT_OPTIONS}
          onMetaChange={(field, value) =>
            setMetaDraft((prev) => ({
              ...prev,
              [field]: field === "assignedTo" ? normalizeAssignedTo(value) : value,
            }))
          }
          onAssignMeta={handleAssignMeta}
          onUnassignMeta={handleUnassignMeta}
          onSaveMeta={handleSaveMeta}
        />
      </div>
    </div>
  );
};

export default TicketWorkspace;
